import { useCallback, useMemo } from 'react'

import { atom, useAtom } from 'jotai'
import { jwtDecode } from 'jwt-decode'

import { useAnalytics } from '@/utils/analytics'
import { getUserDetailsFromCurrentToken, isTokenValid } from '@/utils/auth'
import { addRumScope, removeRumScope } from '@/utils/monitoring'
import { removeItem, setItem } from '@/utils/storage'

import { tokenAtom } from './auth.store'
import { useInitialData } from './useInitialData'
import { useLogin } from './useLogin'
import { useLogout } from './useLogout'

const extractPlainTokenFromAuthenticationResponse = (token: string): string => token.replace(/^JWT /, '').trim()

type LoginProps = {
  email: string
  password: string
}

type JwtTokenData = {
  customerId: string
  userId: string
  loggedInAsCustomer?: boolean
  tokenRequestedFrom?: string
}

const isAuthenticatedAtom = atom<boolean>(false)
const loggedInAsCustomerAtom = atom<boolean>(false)
const tokenRequestedFromAtom = atom<string | undefined>('')

export const useAuth = () => {
  const [token, setToken] = useAtom(tokenAtom)
  const [isAuthenticated, setIsAuthenticated] = useAtom(isAuthenticatedAtom)
  const [loggedInAsCustomer, setLoggedInAsCustomer] = useAtom(loggedInAsCustomerAtom)
  const [tokenRequestedFrom, setTokenRequestedFrom] = useAtom(tokenRequestedFromAtom)
  const loginMutation = useLogin()
  const logoutMutation = useLogout()
  const initialData = useInitialData()
  const analyticsService = useAnalytics()

  const unAuthenticate = useCallback(() => {
    setLoggedInAsCustomer(false)
    setIsAuthenticated(false)
    setToken(undefined)
    removeItem('token')
    removeRumScope()
    analyticsService.resetAnalytics()
  }, [analyticsService, setIsAuthenticated, setLoggedInAsCustomer, setToken])

  const authenticate = useCallback(
    (token: string) => {
      const tokenData = jwtDecode<JwtTokenData>(token)

      setIsAuthenticated(true)
      setToken(token)
      setItem('token', token)
      setLoggedInAsCustomer(tokenData.loggedInAsCustomer ?? false)
      setTokenRequestedFrom(tokenData.tokenRequestedFrom)
    },
    [setIsAuthenticated, setLoggedInAsCustomer, setToken, setTokenRequestedFrom]
  )

  const loginAsCustomer = async (token: string) => {
    setLoggedInAsCustomer(true)
    authenticate(token)
    await fetchInitialData()
  }

  const fetchInitialData = useCallback(async () => {
    const initialDataResult = await initialData.refetch()
    const customer = initialDataResult.data?.customer
    const userIdFromToken = getUserDetailsFromCurrentToken()?.userId
    const user = userIdFromToken ? customer?.users.find((user) => user._id === userIdFromToken) : undefined
    if (user && customer) {
      addRumScope({
        id: user._id,
        email: user.email,
        company: customer.name,
        customerId: customer._id,
      })
      analyticsService.initAnalytics(customer, user, loggedInAsCustomer, tokenRequestedFrom)
    }
  }, [analyticsService, initialData, loggedInAsCustomer, tokenRequestedFrom])

  const login = useCallback(
    async (body: LoginProps) => {
      await loginMutation.mutateAsync(
        {
          email: body.email,
          password: body.password,
        },
        {
          onSuccess: async (data) => {
            const token = extractPlainTokenFromAuthenticationResponse(data.token)
            authenticate(token)
            await fetchInitialData()
          },
        }
      )
    },
    [loginMutation, authenticate, fetchInitialData]
  )

  const logout = useCallback(async () => {
    await unAuthenticate()
    await logoutMutation.mutateAsync(undefined)
  }, [logoutMutation, unAuthenticate])

  const user = useMemo(() => {
    const userIdFromToken = getUserDetailsFromCurrentToken()?.userId
    return initialData.data?.customer?.users.find((user) => user._id === userIdFromToken)
  }, [initialData])

  return {
    token,
    login,
    logout,
    authenticate,
    unAuthenticate,
    loginAsCustomer,
    isReady: !initialData.isPending,
    isLoggingIn: loginMutation.isPending || (isAuthenticated && !user),
    isLoggedIn: isAuthenticated && !!user,
    shouldHideLogin: initialData.isPending || (!!token && isTokenValid(token)),
    initialData,
    customer: initialData.data?.customer,
    user,
    isAuthenticated,
    loggedInAsCustomer,
    tokenRequestedFrom,
    isTokenValid: isTokenValid(token),
  }
}
